import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLessons = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetch = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/lessons`, { headers });
      setLessons(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Session expired');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch lessons');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/lessons/${id}`, { headers });
      toast.success('Lesson deleted');
      fetch();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm('This will generate 1 lesson per topic (5 total) via the LLM. Continue?')) return;
    setGenerating(true);
    try {
      const headers = getAuthHeaders();
      toast.info('Generating 5 lessons (1 per topic). This may take ~1 minute…');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/lessons/generate`,
        { count_per_topic: 1 },
        { headers, timeout: 180000 }
      );
      toast.success(`Created ${res.data.lessons_created} lessons`);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/admin/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-black">
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
          <h1 className="text-2xl font-light">Lessons</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-light">Manage Lessons</h2>
          <div className="flex items-center gap-4">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              data-testid="generate-lessons-btn"
            >
              {generating ? (
                <><Loader2 size={20} className="animate-spin" /><span>Generating...</span></>
              ) : (
                <><Sparkles size={20} /><span>Generate Today's Lessons</span></>
              )}
            </button>
            <Link
              to="/admin/lesson/new"
              className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800"
              data-testid="new-lesson-btn"
            >
              <Plus size={20} />
              <span>New Lesson</span>
            </Link>
          </div>
        </div>

        {lessons.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-600 mb-4">No lessons yet</p>
            <p className="text-sm text-gray-500">Click "Generate Today's Lessons" to auto-create 5 lessons (one per topic), or add one manually.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Topic</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lessons.map(l => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{l.title}</div>
                      <div className="text-sm text-gray-500">{(l.summary || '').slice(0, 80)}...</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{l.topic}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(l.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full ${l.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {l.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/learn/${l.slug}`} className="p-2 text-gray-600 hover:text-black" title="View">
                          <Eye size={18} />
                        </Link>
                        <Link to={`/admin/lesson/${l.id}`} className="p-2 text-gray-600 hover:text-black" title="Edit">
                          <Edit size={18} />
                        </Link>
                        <button onClick={() => handleDelete(l.id, l.title)} className="p-2 text-gray-600 hover:text-red-600" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminLessons;
